import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/http';
import { useApi } from '@/api/provider';
import { STALE_TIMES } from '@/api/queryClient';
import { queryKeys } from '@/api/queryKeys';
import type { BackgroundPushStatus, NotificationPreferences } from '@/api/types';
import { localZone } from '@/features/calendar/zone';
import { isDesktopApp } from '@/utils/desktop';
import { MAX_TIMER_MS, markFired, wasFired } from './calendarReminders';
import { createLeaderElection, LEADER_CHANNEL_NAME } from './leader';
import {
  alreadyShown,
  BACKGROUND_PUSH_QUERY,
  hasPushSubscription,
  notificationsSupported,
  raiseReminderNotice,
  serviceWorkerRegistration,
} from './reminderNotice';
import {
  planTaskReminders,
  taskReminderBody,
  taskReminderStale,
  taskReminderUrl,
  taskRemindersAllowed,
  type PlannedTaskReminder,
} from './taskReminders';

/**
 * The open app's own task reminders — the fallback to the server's push.
 *
 * The server pushes each task reminder at its `remindAt` to every push
 * subscription of the owner. It needs no stored password (it computed the
 * time itself and reads nothing from the mail server), so unlike calendar
 * reminders this does not depend on background notifications being on. What
 * it cannot reach is a browser without a subscription — never registered, or
 * the desktop app, whose webview has no Web Push at all — and those hear about
 * a reminder only if the open app raises it. This does, once per mailbox (the
 * shell mounts one per account with a working password), modelled line for
 * line on `useCalendarReminders`.
 *
 * - **Data.** The server's `view=reminders` read: open tasks whose reminder is
 *   still worth sending, soonest first. Its key is the Tasks screen's own
 *   smart-view key (`taskSmart('reminders', '')`), under `tasksScope`, so a
 *   change made here (the screen invalidates `…tasks/smart` after each write)
 *   or on another device (`tasks_changed` invalidates the whole scope,
 *   realtime/apply.ts) re-plans at once; otherwise it refetches every five
 *   minutes. A mailbox whose tasks answer with a refusal that will not change
 *   (a 4xx such as an older server's unknown view, or a 501 with no task
 *   store) is skipped quietly: no retry, no refetch.
 * - **One window.** Every window runs this and only the elected one sets
 *   timers, per mailbox. The fired set is the calendar's (calendarReminders.ts),
 *   shared through storage; task tags cannot collide with calendar ones.
 * - **Checked when it fires, not when it is planned**: permission, the
 *   preferences (notifications on, task reminders on, no snooze, not quiet
 *   hours), whether the server now covers this browser, and whether the push
 *   already put this notice on screen.
 * - **Stale fifteen minutes after `remindAt`** — see `planTaskReminders` for
 *   why the fallback does not use the server's longer window.
 * - **Shown even while the app is in front**, like calendar reminders.
 */

/** Re-read the reminders (and re-plan the ones beyond the horizon) this often. */
const REFRESH_MS = 5 * 60_000;

/** A refusal that asking again will not change. */
function permanentlyRefused(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.status === 501) return true;
  return error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429;
}

/**
 * Whether the server will push this reminder to this browser, so the fallback
 * stands aside.
 *
 * Three things, all needed. Not the desktop app: its webview has no Web Push,
 * whatever the server says. GET /push/background must say the server runs the
 * task reminder scheduler (`taskReminders`) — an older server omits it, and
 * standing aside there would leave no reminder at all. Its `status` is NOT
 * consulted: task reminders go to every subscription whether or not a mailbox
 * is enrolled for background notifications. And this browser must hold a
 * subscription for the push to land on.
 */
async function serverCoversThisBrowser(
  client: QueryClient,
  registration: ServiceWorkerRegistration | undefined,
): Promise<boolean> {
  if (isDesktopApp()) return false;
  const status = client.getQueryData<BackgroundPushStatus>(BACKGROUND_PUSH_QUERY);
  if (status?.taskReminders !== true) return false;
  return hasPushSubscription(registration);
}

interface FireContext {
  client: QueryClient;
  accountId: string;
  zone: string;
  navigate: RefObject<NavigateFunction>;
}

/** Raises one task reminder, if everything that can say no says yes. */
async function fireTaskReminder(item: PlannedTaskReminder, context: FireContext): Promise<void> {
  const { client, accountId, zone } = context;
  const now = Date.now();
  // A timer that fired late (a laptop waking hours on) must not dig it up.
  if (taskReminderStale(item, now) || wasFired(item.key)) return;
  if (!notificationsSupported() || Notification.permission !== 'granted') return;
  const prefs = client.getQueryData<NotificationPreferences>(
    queryKeys.account(accountId).notificationPrefs(),
  );
  if (!taskRemindersAllowed(prefs, new Date(now), zone)) return;

  const registration = await serviceWorkerRegistration();
  if (await serverCoversThisBrowser(client, registration)) return;
  if (await alreadyShown(registration, item.tag, now)) {
    markFired(item.key, now);
    return;
  }
  // Checked again after the awaits: another path may have got here meanwhile.
  if (wasFired(item.key)) return;
  markFired(item.key, now);

  const { task, title, tag } = item;
  await raiseReminderNotice(
    {
      // The full title, even where the push is minimal ("Task reminder"): the
      // minimal push guards a lock screen against the push service's route,
      // and this notice never leaves the device.
      title,
      body: taskReminderBody(task, new Date(now)),
      tag,
      url: taskReminderUrl(accountId, task.listId, task.id),
      now,
    },
    registration,
    context.navigate,
  );
}

export function useTaskReminders(accountId: string | undefined): void {
  const api = useApi();
  const client = useQueryClient();
  const id = accountId ?? '';
  const zone = useMemo(() => localZone(), []);

  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const prefs = useQuery({
    queryKey: queryKeys.account(id).notificationPrefs(),
    queryFn: ({ signal }) => api.push.getPreferences(id, signal),
    enabled: id !== '',
    staleTime: STALE_TIMES.metadata,
  });
  // Off means no task reads at all. Checked again at fire time, because it can
  // change while a timer waits.
  const wanted =
    id !== '' &&
    notificationsSupported() &&
    prefs.data?.enabled === true &&
    prefs.data.taskReminders === true;

  // Re-plans every few minutes even when the data has not changed, so a
  // reminder that was beyond the scheduling horizon gets its timer.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!wanted) return;
    const timer = setInterval(() => setTick((current) => current + 1), REFRESH_MS);
    return () => clearInterval(timer);
  }, [wanted]);

  const reminders = useQuery({
    queryKey: queryKeys.account(id).taskSmart('reminders', ''),
    queryFn: ({ signal }) => api.tasks.smart(id, 'reminders', '', signal),
    enabled: wanted,
    staleTime: STALE_TIMES.list,
    refetchInterval: (query) => (permanentlyRefused(query.state.error) ? false : REFRESH_MS),
    // A hidden tab is exactly where a reminder is needed most.
    refetchIntervalInBackground: true,
    // Nothing to remind about is the answer either way; the next round asks
    // again unless the refusal is one that will not change.
    retry: false,
  });

  // Read by `serverCoversThisBrowser` from the cache; fetched here so it is
  // there even if the settings page was never opened.
  useQuery({
    queryKey: BACKGROUND_PUSH_QUERY,
    queryFn: ({ signal }) => api.push.getBackgroundPush(signal),
    enabled: wanted && !isDesktopApp(),
    staleTime: STALE_TIMES.metadata,
    retry: false,
  });

  const [leading, setLeading] = useState(false);
  useEffect(() => {
    if (!wanted) return;
    const election = createLeaderElection({
      channelName: `${LEADER_CHANNEL_NAME}:task-reminders:${id}`,
      onChange: setLeading,
    });
    const onVisibility = (): void => election.refresh();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      election.stop();
      setLeading(false);
    };
  }, [wanted, id]);

  const data = reminders.data;
  useEffect(() => {
    if (!wanted || !leading || data === undefined) return;
    const now = Date.now();
    const context: FireContext = { client, accountId: id, zone, navigate: navigateRef };
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (const item of planTaskReminders(data, now)) {
      if (wasFired(item.key)) continue;
      const delay = Math.min(Math.max(item.triggerAt - now, 0), MAX_TIMER_MS);
      timers.push(
        setTimeout(() => {
          if (!cancelled) void fireTaskReminder(item, context);
        }, delay),
      );
    }
    return () => {
      cancelled = true;
      for (const timer of timers) clearTimeout(timer);
    };
  }, [wanted, leading, data, client, id, zone, tick]);
}

/** Mounted by the shell once per mailbox. Renders nothing. */
export function TaskReminders({ accountId }: { accountId: string }): null {
  useTaskReminders(accountId);
  return null;
}
